import { qnaActionStyle } from "./qnaStyles";
import { Fragment, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Plus,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import excelIcon from "@/assets/excel-icon.png";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import type {
  QnaQuestion,
  QuestionCreate,
  QuestionUpdate,
} from "@/lib/api/fddQa";

const DEFAULT_COL_WIDTHS = [32, 60, 80, 280, 120, 160, 180, 50];
const HEADERS = [
  "#",
  "Priority",
  "Area",
  "Question",
  "Source",
  "Response",
  "AI Check",
  "",
];
const PRIORITIES = [
  { label: "All", color: null },
  { label: "New", color: "#3b82f6" },
  { label: "Critical", color: "#ef4444" },
  { label: "High", color: "#f97316" },
  { label: "Medium", color: "#eab308" },
  { label: "Low", color: "#22c55e" },
];
const OUTCOMES = [
  { label: "Closed", key: "clear", color: "#22c55e" },
  { label: "Follow-up", key: "follow-up", color: "#3b82f6" },
  { label: "Adjustment", key: "deal-implication", color: "#e11d48" },
  { label: "Analysis", key: "request-analysis", color: "#8b5cf6" },
  { label: "New RFI", key: "request-info", color: "#d97706" },
];

function isNew(question: QnaQuestion) {
  return question.revision === 0 && question.status === "Open";
}
function savedOutcome(question: QnaQuestion) {
  if (question.deal_implication) return "deal-implication";
  if (question.follow_up) return "follow-up";
  if (question.reviewed || question.status === "Closed") return "clear";
  return null;
}

type Field = "question" | "answer" | "deal_implication";
interface Props {
  questions: QnaQuestion[];
  canEdit: boolean;
  busy: boolean;
  reviewingId?: string | null;
  actions?: ReactNode;
  responseActions?: ReactNode;
  summary?: ReactNode;
  getOutcome?: (question: QnaQuestion) => string | null;
  renderAiCheck?: (question: QnaQuestion) => ReactNode;
  onOpenQuestion?: (question: QnaQuestion) => void;
  sheetNames?: string[];
  onAdd?: (question: QuestionCreate) => Promise<void>;
  onContinue?: () => Promise<void>;
  onUpdate: (question: QnaQuestion, update: QuestionUpdate) => Promise<void>;
  onReview?: (question: QnaQuestion) => Promise<void>;
  onOpenSheet?: (sheet: string) => void;
}

export function QnaQuestionsList({
  questions,
  canEdit,
  busy,
  reviewingId,
  actions,
  responseActions,
  summary,
  getOutcome = savedOutcome,
  renderAiCheck,
  onOpenQuestion,
  sheetNames,
  onAdd,
  onContinue,
  onUpdate,
  onReview,
  onOpenSheet,
}: Props) {
  const [filter, setFilter] = useState("All");
  const [showRejected, setShowRejected] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [widths, setWidths] = useState(DEFAULT_COL_WIDTHS);
  const resize = useRef<{ index: number; x: number; width: number } | null>(
    null,
  );
  const [searchColumn, setSearchColumn] = useState<number | null>(null);
  const [search, setSearch] = useState<Record<number, string>>({});
  const [editing, setEditing] = useState<{
    item: QnaQuestion;
    field: Field;
    draft: string;
  } | null>(null);
  const saving = useRef(false);
  const cancelling = useRef(false);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({
    question: "",
    metric: "",
    databook_tab: "",
    answer: "",
  });
  const [continueOpen, setContinueOpen] = useState(false);
  const [notesConfirmed, setNotesConfirmed] = useState(false);
  const all = questions.filter((q) => showRejected || q.status !== "Rejected");
  const groups = new Map<string, QnaQuestion[]>();
  const sheets = sheetNames ?? [
    ...new Set(questions.map((q) => q.databook_tab)),
  ];
  for (const question of all) {
    const matches =
      filter === "All" ||
      (filter === "New"
        ? isNew(question)
        : question.priority === filter || getOutcome(question) === filter);
    if (!matches) continue;
    const values = [
      String(all.indexOf(question) + 1),
      question.priority,
      question.metric,
      question.question,
      question.databook_tab,
      question.answer ?? "",
      getOutcome(question) ?? "",
      "",
    ];
    if (
      !Object.entries(search).every(([column, value]) =>
        values[Number(column)].toLowerCase().includes(value.toLowerCase()),
      )
    )
      continue;
    const group = groups.get(question.databook_tab) ?? [];
    group.push(question);
    groups.set(question.databook_tab, group);
  }
  async function save() {
    if (!editing || saving.current || cancelling.current || busy) return;
    const value = editing.draft.trim();
    if (editing.field === "question" && !value) return;
    if (value === (editing.item[editing.field] ?? "")) {
      setEditing(null);
      return;
    }
    saving.current = true;
    try {
      await onUpdate(editing.item, { [editing.field]: value || null });
      setEditing(null);
    } catch {
      /* Keep the draft; the stage reports the failed save. */
    } finally {
      saving.current = false;
    }
  }
  function editable(
    item: QnaQuestion,
    field: Field,
    placeholder: string,
    inThread = false,
  ) {
    if (
      editing?.item.id === item.id &&
      editing.field === field &&
      !inThread
    )
      return (
        <Textarea
          aria-label={`Edit ${field.replace(/_/g, " ")}`}
          value={editing.draft}
          autoFocus
          className="text-xs min-h-[60px] resize-none"
          maxLength={field === "answer" ? 50000 : 20000}
          onChange={(event) =>
            setEditing({ ...editing, draft: event.target.value })
          }
          onBlur={() => void save()}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              cancelling.current = true;
              setEditing(null);
            }
          }}
        />
      );
    return (
      <button
        type="button"
        aria-label={`Edit ${field.replace(/_/g, " ")}: ${item.metric}`}
        disabled={!canEdit || busy || Boolean(editing)}
        className={cn(
          "whitespace-pre-line select-text cursor-text hover:bg-muted/40 rounded px-1 -mx-1 py-0.5 transition-colors flex-1 text-left",
          !item[field] && "text-muted-foreground italic",
        )}
        onClick={(event) => {
          event.stopPropagation();
          cancelling.current = false;
          setEditing({ item, field, draft: item[field] ?? "" });
        }}
      >
        {item[field] || placeholder}
      </button>
    );
  }
  function filterButton(
    label: string,
    key: string,
    color: string | null,
    count: number,
  ) {
    const active = filter === key;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={active}
        disabled={Boolean(editing)}
        onClick={() => setFilter(active ? "All" : key)}
        style={{
          padding: "4px 10px",
          borderRadius: 6,
          border: active
            ? `1px solid ${color ? color + "55" : "#ccc"}`
            : "1px solid transparent",
          background: active ? (color ? color + "10" : "#fff") : "transparent",
          color: active ? color || "#1a1a1a" : "#888",
          fontSize: 12,
          fontWeight: active ? 600 : 400,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: active ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
          transition: "all 0.15s",
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontFamily: "inherit",
        }}
      >
        {color && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: color,
              flexShrink: 0,
            }}
          />
        )}
        {count} {label}
      </button>
    );
  }
  return (
    <div className="space-y-6">
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #E5E5E5",
          padding: "14px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-1 flex-wrap items-center gap-1.5">
            {PRIORITIES.map(({ label, color }) =>
              filterButton(
                label,
                label,
                color,
                label === "All"
                  ? all.length
                  : all.filter((q) =>
                      label === "New" ? isNew(q) : q.priority === label,
                    ).length,
              ),
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              disabled={
                !onContinue ||
                busy ||
                Boolean(editing) ||
                !all.some((q) => q.answer?.trim())
              }
              onClick={() => setContinueOpen(true)}
              style={{
                padding: "6px 18px",
                borderRadius: 8,
                border: "none",
                background: "#1a1a1a",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontFamily: "inherit",
                flexShrink: 0,
              }}
              className="disabled:opacity-50"
            >
              Continue <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {OUTCOMES.map(({ label, key, color }) =>
            filterButton(
              label,
              key,
              color,
              all.filter((q) => getOutcome(q) === key).length,
            ),
          )}
        </div>
        <div
          className="flex items-center gap-2 overflow-x-auto"
          style={{ borderTop: "1px solid #E5E5E5", paddingTop: 12 }}
        >
          {actions}
          {responseActions ??
            (
              [
                ["X", "#217346", "Upload Q&A Responses"],
                ["P", "#D24726", "Make an agenda"],
              ] as const
            ).map(([letter, color, label]) => (
              <button
                key={label}
                type="button"
                disabled
                title="Coming soon"
                style={{
                  ...qnaActionStyle,
                  background: "#fafafa",
                  color: "#bbb",
                  cursor: "not-allowed",
                  opacity: 0.5,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="14"
                    height="14"
                    rx="2"
                    fill={color}
                    opacity="0.4"
                  />
                  <text
                    x="8"
                    y="11.5"
                    textAnchor="middle"
                    fontSize="9"
                    fontWeight="700"
                    fill="#fff"
                  >
                    {letter}
                  </text>
                </svg>
                {label}
              </button>
            ))}
          {canEdit && onAdd && (
            <button
              type="button"
              disabled={busy || Boolean(editing) || !sheets.length}
              style={qnaActionStyle}
              onClick={() => {
                setDraft({
                  question: "",
                  metric: "",
                  databook_tab: sheets[0] ?? "",
                  answer: "",
                });
                setAddOpen(true);
              }}
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
              Add Q&A
            </button>
          )}
        </div>
        {questions.some((q) => q.status === "Rejected") && (
          <label className="flex items-center gap-2 pt-1 text-[10px] text-muted-foreground">
            <input
              type="checkbox"
              checked={showRejected}
              onChange={(e) => setShowRejected(e.target.checked)}
            />
            Show rejected
          </label>
        )}
      </div>
      {summary}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-xs"
            style={{ tableLayout: "fixed", minWidth: 962 }}
          >
            <colgroup>
              {widths.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              {searchColumn !== null && (
                <tr className="border-b border-border bg-muted/30">
                  {HEADERS.map((header, index) => (
                    <td key={index} className="px-1 py-1">
                      {searchColumn === index && (
                        <input
                          aria-label={`Search ${header}`}
                          autoFocus
                          value={search[index] ?? ""}
                          onChange={(e) =>
                            setSearch({ ...search, [index]: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setSearchColumn(null);
                              setSearch({ ...search, [index]: "" });
                            }
                          }}
                          placeholder="Search…"
                          className="w-full text-[10px] bg-background border border-border rounded px-1.5 py-0.5 outline-none focus:border-ring"
                        />
                      )}
                    </td>
                  ))}
                </tr>
              )}
              <tr className="border-b border-border bg-muted/50">
                {HEADERS.map((label, index) => (
                  <th
                    key={index}
                    className="relative px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider select-none group/th"
                    style={{ overflow: "visible" }}
                  >
                    {label && (
                      <button
                        type="button"
                        title="Click to search this column"
                        disabled={Boolean(editing)}
                        onClick={() =>
                          setSearchColumn(searchColumn === index ? null : index)
                        }
                        className={cn(
                          "cursor-pointer inline-flex items-center gap-1 hover:text-foreground transition-colors uppercase tracking-wider",
                          searchColumn === index && "text-foreground",
                          search[index] && "text-primary",
                        )}
                      >
                        {label}
                        <Search
                          className={cn(
                            "w-3 h-3 opacity-0 group-hover/th:opacity-60 transition-opacity",
                            searchColumn === index && "opacity-80",
                          )}
                        />
                        {search[index] && (
                          <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                        )}
                      </button>
                    )}
                    {index < 7 && (
                      <span
                        role="separator"
                        aria-label={`Resize ${label}`}
                        aria-orientation="vertical"
                        aria-valuemin={32}
                        aria-valuemax={2000}
                        aria-valuenow={widths[index]}
                        tabIndex={0}
                        className="absolute right-0 top-0 h-full w-2 cursor-col-resize opacity-0 group-hover/th:opacity-100 focus:opacity-100 flex items-center justify-center hover:opacity-100 transition-opacity touch-none"
                        style={{ zIndex: 10 }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                            e.preventDefault();
                            setWidths((previous) =>
                              previous.map((width, i) =>
                                i === index
                                  ? Math.max(
                                      40,
                                      width +
                                        (e.key === "ArrowRight" ? 10 : -10),
                                    )
                                  : width,
                              ),
                            );
                          }
                        }}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          resize.current = {
                            index,
                            x: e.clientX,
                            width: widths[index],
                          };
                          e.currentTarget.setPointerCapture(e.pointerId);
                        }}
                        onPointerMove={(e) => {
                          const start = resize.current;
                          if (start)
                            setWidths((previous) =>
                              previous.map((width, i) =>
                                i === start.index
                                  ? Math.max(
                                      40,
                                      start.width + e.clientX - start.x,
                                    )
                                  : width,
                              ),
                            );
                        }}
                        onPointerUp={() => {
                          resize.current = null;
                        }}
                        onLostPointerCapture={() => {
                          resize.current = null;
                        }}
                      >
                        <span className="w-px h-4 bg-border/80" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...groups].map(([sheet, rows]) => (
                <Fragment key={sheet}>
                  <tr className="border-b border-border bg-muted/30 hover:bg-muted/50 transition-colors">
                    <td colSpan={8} className="px-3 py-2.5">
                      <button
                        type="button"
                        disabled={Boolean(editing)}
                        aria-expanded={!collapsed.has(sheet)}
                        className="flex w-full items-center gap-2 text-left"
                        onClick={() =>
                          setCollapsed((previous) => {
                            const next = new Set(previous);
                            if (next.has(sheet)) next.delete(sheet);
                            else next.add(sheet);
                            return next;
                          })
                        }
                      >
                        {collapsed.has(sheet) ? (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className="text-xs font-semibold text-foreground tracking-wide">
                          {sheet}
                        </span>
                        <span className="text-[10px] text-muted-foreground font-medium">
                          {rows.filter((q) => q.answer?.trim()).length}/
                          {rows.length} answered
                        </span>
                      </button>
                    </td>
                  </tr>
                  {!collapsed.has(sheet) &&
                    rows.map((item, index) => (
                      <tr
                        key={item.id}
                        onClick={() => !editing && onOpenQuestion?.(item)}
                        className={cn(
                          "border-b border-border hover:bg-muted/20 transition-colors group cursor-pointer",
                          "",
                        )}
                      >
                        <td className="px-2 py-2 font-mono text-foreground/60 align-top">
                          {index + 1}
                        </td>
                        <td
                          className="px-2 py-2 align-top"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            aria-label={`Priority: ${item.metric}`}
                            value={item.priority}
                            disabled={!canEdit || busy || Boolean(editing)}
                            onChange={(e) =>
                              void onUpdate(item, {
                                priority: e.target
                                  .value as QnaQuestion["priority"],
                              }).catch(() => {})
                            }
                            className={cn(
                              "text-[9px] font-semibold px-1 py-0.5 rounded border cursor-pointer appearance-none text-center",
                              item.priority === "Critical"
                                ? "text-red-600 border-red-300"
                                : item.priority === "High"
                                  ? "text-orange-600 border-orange-300"
                                  : item.priority === "Low"
                                    ? "text-[#8A8A84] border-[#E5E5E5]"
                                    : "text-[#52524E] border-[#E5E5E5]",
                            )}
                            style={{ minWidth: 52, background: "white" }}
                          >
                            {["Critical", "High", "Medium", "Low"].map(
                              (priority) => (
                                <option key={priority}>{priority}</option>
                              ),
                            )}
                          </select>
                        </td>
                        <td
                          className="px-2 py-2 text-foreground/85 align-top"
                          style={{
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                        >
                          {item.metric}
                        </td>
                        <td
                          className="px-2 py-2 text-foreground/85 align-top"
                          style={{
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-start gap-1.5">
                            {editable(item, "question", "")}
                          </div>
                          {item.is_custom && (
                            <span className="inline-block mt-1 text-[9px] font-medium text-primary/60 bg-primary/10 px-1.5 py-0.5 rounded">
                              Custom
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <button
                            type="button"
                            disabled={!onOpenSheet || Boolean(editing)}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenSheet?.(sheet);
                            }}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors text-[10px] border-border/50 bg-muted/20 hover:bg-muted/40 text-foreground/70 hover:text-foreground"
                          >
                            <img
                              src={excelIcon}
                              alt=""
                              className="w-3.5 h-3.5 shrink-0"
                            />
                            <span className="truncate max-w-[100px]">
                              {sheet}
                            </span>
                          </button>
                        </td>
                        <td
                          className="px-2 py-2 align-top text-foreground/70"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-start gap-1 group/response">
                            {editable(item, "answer", "Click to respond")}
                            {item.answer && (
                              <button
                                type="button"
                                title="Copy response"
                                aria-label="Copy response"
                                className="opacity-0 group-hover/response:opacity-100 focus:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted/50"
                                onClick={() =>
                                  void navigator.clipboard
                                    .writeText(item.answer!)
                                    .then(() =>
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Response copied to clipboard.",
                                      }),
                                    )
                                    .catch(() =>
                                      toast({
                                        title: "Could not copy response",
                                        variant: "destructive",
                                      }),
                                    )
                                }
                              >
                                <Copy className="w-3 h-3 text-muted-foreground" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-top">
                          {renderAiCheck ? renderAiCheck(item) : reviewingId === item.id ? (
                            <div className="flex items-center gap-1.5">
                              <Loader2 className="w-3 h-3 text-primary animate-spin shrink-0" />
                              <span className="text-[10px] text-muted-foreground">
                                Checking…
                              </span>
                            </div>
                          ) : !item.answer ? (
                            <span className="text-muted-foreground text-[10px]">
                              —
                            </span>
                          ) : (
                            <>
                              <div className="flex items-center justify-between gap-1 flex-wrap">
                                {getOutcome(item) && (
                                  <button
                                    type="button"
                                    className={cn(
                                      "text-[10px] font-medium px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80 bg-white border",
                                      item.deal_implication
                                        ? "text-rose-600 border-rose-200"
                                        : item.follow_up
                                          ? "text-blue-600 border-blue-200"
                                          : "text-emerald-600 border-emerald-200",
                                    )}
                                  >
                                    {item.deal_implication
                                      ? "Adjustment"
                                      : item.follow_up
                                        ? "Follow-up"
                                        : "Resolved"}
                                  </button>
                                )}
                                {item.deal_implication && item.follow_up && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-blue-600 bg-blue-50">
                                    Follow-up
                                  </span>
                                )}
                                <button
                                  type="button"
                                  aria-label="Copy AI check"
                                  title="Copy AI check"
                                  className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void navigator.clipboard.writeText(
                                      [item.deal_implication, item.follow_up].filter(Boolean).join("\n\n"),
                                    ).then(() => toast({ title: "Copied", description: "AI check copied to clipboard." })).catch(() => toast({ title: "Could not copy AI check", variant: "destructive" }));
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                              {item.deal_implication && (
                                <div className="mt-1.5 select-text text-[10px] text-foreground/70 leading-relaxed border-l-2 border-rose-300 pl-2">
                                  {item.deal_implication}
                                </div>
                              )}
                              {item.follow_up && (
                                <div className="mt-1.5 select-text text-[10px] text-foreground/70 leading-relaxed border-l-2 border-blue-300 pl-2">
                                  {item.follow_up}
                                </div>
                              )}
                              {canEdit && onReview && (
                                <button
                                  type="button"
                                  disabled={busy || Boolean(editing)}
                                  className="mt-1 text-[9px] text-muted-foreground hover:text-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void onReview(item).catch(() => {});
                                  }}
                                >
                                  {item.reviewed
                                    ? "Review again"
                                    : "Review response"}
                                </button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-2 py-2 align-top">
                          {canEdit && (
                            <div className="flex items-center gap-0.5 pt-0.5">
                              <button
                                type="button"
                                disabled={busy || Boolean(editing)}
                                aria-label={
                                  item.status === "Rejected"
                                    ? "Restore question"
                                    : "Accept question"
                                }
                                title={
                                  item.status === "Accepted"
                                    ? "Accepted"
                                    : item.status === "Rejected"
                                      ? "Restore question"
                                      : "Accept question"
                                }
                                className={cn(
                                  "p-1 rounded transition-colors",
                                  item.status === "Accepted"
                                    ? "bg-emerald-100"
                                    : "hover:bg-success-bg",
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void onUpdate(item, {
                                    status:
                                      item.status === "Rejected"
                                        ? "Open"
                                        : "Accepted",
                                  }).catch(() => {});
                                }}
                              >
                                <Check
                                  className={cn(
                                    "w-3 h-3",
                                    item.status === "Accepted"
                                      ? "text-emerald-600"
                                      : "text-success",
                                  )}
                                />
                              </button>
                              <button
                                type="button"
                                disabled={busy || Boolean(editing)}
                                aria-label="Reject question"
                                title="Reject question"
                                className="p-1 rounded hover:bg-destructive/10 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void onUpdate(item, {
                                    status: "Rejected",
                                  }).catch(() => {});
                                }}
                              >
                                <X className="w-3 h-3 text-destructive" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          {!groups.size && (
            <p className="p-8 text-center text-xs text-muted-foreground">
              {questions.length
                ? "No questions match these filters."
                : "No saved Q&A for this databook version."}
            </p>
          )}
        </div>
      </div>
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!busy) setAddOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Your Own Q&A</DialogTitle>
            <DialogDescription>
              Add a custom question and optionally pre-fill the answer.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              void onAdd?.({ ...draft, answer: draft.answer.trim() || null })
                .then(() => setAddOpen(false))
                .catch(() => {});
            }}
          >
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              Databook sheet
              <select
                aria-label="Databook sheet"
                className="w-full text-sm border border-border rounded px-3 py-2 bg-background text-foreground"
                value={draft.databook_tab}
                onChange={(e) =>
                  setDraft({ ...draft, databook_tab: e.target.value })
                }
              >
                {sheets.map((sheet) => (
                  <option key={sheet}>{sheet}</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              Area
              <input
                required
                maxLength={512}
                className="w-full text-sm border border-border rounded px-3 py-2 bg-background text-foreground"
                value={draft.metric}
                onChange={(e) => setDraft({ ...draft, metric: e.target.value })}
              />
            </label>
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              Question
              <Textarea
                required
                maxLength={20000}
                className="min-h-[80px] text-sm"
                placeholder="Type your question here..."
                value={draft.question}
                onChange={(e) =>
                  setDraft({ ...draft, question: e.target.value })
                }
              />
            </label>
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              Response (optional)
              <Textarea
                maxLength={50000}
                className="min-h-[60px] text-sm"
                placeholder="Pre-fill a response if already known..."
                value={draft.answer}
                onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  busy || !draft.question.trim() || !draft.metric.trim()
                }
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={continueOpen}
        onOpenChange={(open) => {
          if (!busy) {
            setContinueOpen(open);
            if (!open) setNotesConfirmed(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Before you continue
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Please confirm before generating deal implications.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-3 py-4 text-sm">
            <input
              type="checkbox"
              checked={notesConfirmed}
              onChange={(e) => setNotesConfirmed(e.target.checked)}
            />
            I confirm that all meeting notes have been uploaded.
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setContinueOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || !notesConfirmed}
              onClick={() =>
                void onContinue?.()
                  .then(() => {
                    setContinueOpen(false);
                    setNotesConfirmed(false);
                  })
                  .catch(() => {})
              }
            >
              Confirm & Continue
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
